import {domToReact, Element as ParserElement, HTMLReactParserOptions, DOMNode} from 'html-react-parser';
import {map} from 'lodash';
import {useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, ClientDynamicContent, NodeElement, UserPreview} from 'components';
import {DynamicComponentNames, SessionEndedUnits as Units, SESSION_ENDED_DEFAULT_TEMPLATE} from 'constants/client';
import {ClientContext} from 'context';
import {useClientSession} from 'hooks';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {getDuration, dynamicContentUtils} from 'utils';
import {getParticipantName} from 'utils/transform';

import './styles.scss';
import {FeedbackView} from './FeedbackView';

const T_KEYS_CLIENT_IN_SESSION_PAGE = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE;
const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.SESSION_ENDED_VIEW;
const SESSION_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION;

const {isMatchElement, nodeElementParent, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {
        yourAgentLabel, agent, agentName, isError, isNew, duration, sessionEndedMessage,
        canSendFeedback, renderErrors, openFeedbackView, renderAddFeedbackButton, localParticipant
    } = props;
    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;
            const showAgentData = !isError && agent?.data && !isNew;

            if (isMatchElement(Units.Errors, node)) {
                return <NodeElement show={isError} node={node}>{renderErrors()}</NodeElement>;
            }
            if (isMatchElement(Units.Duration, node)) {
                return <NodeElement show={!isError && !isNew} node={node}>{duration}</NodeElement>;
            }
            if (isMatchElement(Units.SessionEndedMessage, node)) {
                return <NodeElement show={!isError} node={node}>{sessionEndedMessage}</NodeElement>;
            }
            if (isMatchElement(Units.AgentInfo, node)) {
                return nodeElementParent({show: showAgentData});
            }
            if (isMatchElement(Units.YourAgentLabel, node)) {
                return <NodeElement show={showAgentData} node={node}>{yourAgentLabel}</NodeElement>;
            }
            if (isMatchElement(Units.AgentPreview, node)) {
                return <NodeElement show={showAgentData} node={node}><UserPreview name={agentName} /></NodeElement>;
            }
            if (isMatchElement(Units.AgentName, node)) {
                return <NodeElement show={showAgentData} node={node}>{agentName}</NodeElement>;
            }
            if (isMatchElement(Units.AddFeedbackButton, node)) {
                return (
                    <NodeElement show={canSendFeedback} node={node} onClick={openFeedbackView}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.AddFeedbackButtonDefault, node)) {
                return <NodeElement show={canSendFeedback} node={node} replace>{renderAddFeedbackButton()}</NodeElement>;
            }
            return renderDefaultNode(node, {role: localParticipant?.role});
        }
    };
};

export const SessionEndedView: React.FC = () => {
    const {t} = useTranslation();
    const {errorMessageTKey} = useContext(ClientContext);
    const {isNew, agent, image, startDate, canSendFeedback} = useClientSession();

    const errorMessages = useMemo(() => {
        if (!errorMessageTKey) {
            return [];
        }
        return [t(errorMessageTKey), t(TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.MESSAGES.CONTACT_PROVIDER)];
    }, [t, errorMessageTKey]);

    const duration = useMemo(() => {
        if (!startDate) {
            return null;
        }
        return getDuration(startDate, new Date());
    }, [startDate]);

    const isError = useMemo(() => Array.isArray(errorMessages) && errorMessages.length > 0, [errorMessages]);
    const [feedbackViewOpen, setFeedbackViewOpen] = useState(false);

    const closeFeedbackView = useCallback(() => {
        setFeedbackViewOpen(false);
    }, []);

    const openFeedbackView = useCallback(() => {
        setFeedbackViewOpen(true);
    }, []);

    const renderAddFeedbackButton = useCallback(() => (
        <Button
            className='add-feedback-button'
            rounded
            color='unstyled'
            onClick={openFeedbackView}
        >
            {t(SESSION_T_KEYS.FEEDBACK.ADD_MY)}
        </Button>
    ), [t, openFeedbackView]);

    const renderErrors = useCallback(() => (
        <div className='errors'>
            {map(errorMessages, (message, index) => <div key={index}>{message}</div>)}
        </div>
    ), [errorMessages]);

    const sessionEndedMessage = useMemo(() => t(T_KEYS.CALL_STATUS), [t]);
    const yourAgentLabel = useMemo(() => t(T_KEYS_CLIENT_IN_SESSION_PAGE.YOUR_AGENT_IS), [t]);
    const agentName = useMemo(() => getParticipantName(agent?.data), [agent]);

    if (feedbackViewOpen) {
        return <FeedbackView onClose={closeFeedbackView} />;
    }

    return (
        <ClientDynamicLayout image={image}>
            <ClientDynamicContent
                component={DynamicComponentNames.SessionEnded}
                defaultTemplate={SESSION_ENDED_DEFAULT_TEMPLATE}
                optionsProps={{
                    agent, yourAgentLabel, agentName, isError, isNew, duration, sessionEndedMessage, canSendFeedback,
                    openFeedbackView, renderErrors, renderAddFeedbackButton
                }}
                optionsGetter={getOptions}
            />
        </ClientDynamicLayout>
    );
};
